import { buildPostingPriceLine, countConnectedSocials } from './postingPlan.js';

function eligibleSwitchState({ billingStatus, accessState, schedulerTarget }) {
  if (billingStatus === 'trialing') {
    return accessState === 'trialing' || accessState === 'cancel_scheduled';
  }
  if (schedulerTarget) {
    return (billingStatus === 'active' || billingStatus === 'checkout_completed')
      && (accessState === 'active' || accessState === 'cancel_scheduled' || accessState === 'downgrade_scheduled');
  }
  return (billingStatus === 'active' || billingStatus === 'checkout_completed' || billingStatus === 'past_due')
    && (accessState === 'active' || accessState === 'grace' || accessState === 'cancel_scheduled' || accessState === 'downgrade_scheduled');
}

export function buildPlanSwitchReview({
  accountId,
  billingStatus,
  accessState,
  currentPlanId,
  cancelAtPeriodEnd,
  scheduledPlan,
  scheduledPlanEffectiveAt,
  targetPlanKey,
  targetName,
  targetPriceCents,
  targetPriceFormatted,
  postingDisplay,
  connectedSocials,
} = {}) {
  const isTrialing = billingStatus === 'trialing';
  const schedulerTarget = targetPlanKey === 'posting';
  if (!accountId
      || !eligibleSwitchState({ billingStatus, accessState, schedulerTarget })
      || !currentPlanId
      || !targetPlanKey
      || currentPlanId === targetPlanKey
      || !targetName
      || typeof cancelAtPeriodEnd !== 'boolean'
      || !Number.isInteger(targetPriceCents)
      || targetPriceCents <= 0
      || typeof targetPriceFormatted !== 'string'
      || !targetPriceFormatted) return null;

  const base = {
    accountId,
    planKey: targetPlanKey,
    targetName,
    targetPriceCents,
    targetPriceFormatted,
    billingStatus,
    accessState,
    currentPlanId,
    cancelAtPeriodEnd,
    scheduledPlan: scheduledPlan || null,
    scheduledPlanEffectiveAt: scheduledPlanEffectiveAt || null,
    isTrialing,
    schedulerTarget,
  };

  if (!schedulerTarget) return base;
  if (!Array.isArray(connectedSocials)
      || postingDisplay?.fromDb !== true
      || postingDisplay.priceCents !== targetPriceCents
      || typeof postingDisplay.currency !== 'string'
      || !postingDisplay.currency) return null;

  const connectedQuantity = countConnectedSocials(connectedSocials);
  const billedQuantity = Math.max(1, connectedQuantity);
  const line = buildPostingPriceLine({
    quantity: billedQuantity,
    unitPriceCents: postingDisplay.priceCents,
    currency: postingDisplay.currency,
  });

  return {
    ...base,
    connectedQuantity,
    billedQuantity,
    unitPriceCents: postingDisplay.priceCents,
    currency: postingDisplay.currency,
    unitFormatted: line.unitFormatted,
    totalFormatted: line.totalFormatted,
  };
}

export function buildSchedulerSwitchReview(input = {}) {
  return buildPlanSwitchReview({
    ...input,
    targetPlanKey: 'posting',
    targetName: input.targetName || 'Scheduler',
    targetPriceCents: input.postingDisplay?.priceCents,
    targetPriceFormatted: input.targetPriceFormatted || (input.postingDisplay?.priceCents
      ? buildPostingPriceLine({ quantity: 1, unitPriceCents: input.postingDisplay.priceCents, currency: input.postingDisplay.currency }).unitFormatted
      : null),
  });
}

export function planSwitchReviewMatches(review, current) {
  if (!review || !current) return false;
  return review.accountId === current.accountId
    && review.planKey === current.planKey
    && review.targetPriceCents === current.targetPriceCents
    && review.billingStatus === current.billingStatus
    && review.accessState === current.accessState
    && review.currentPlanId === current.currentPlanId
    && review.cancelAtPeriodEnd === current.cancelAtPeriodEnd
    && review.scheduledPlan === current.scheduledPlan
    && review.scheduledPlanEffectiveAt === current.scheduledPlanEffectiveAt
    && review.isTrialing === current.isTrialing
    && (!review.schedulerTarget || (
      review.connectedQuantity === current.connectedQuantity
      && review.billedQuantity === current.billedQuantity
      && review.unitPriceCents === current.unitPriceCents
      && review.currency === current.currency
    ));
}
export const schedulerSwitchReviewMatches = planSwitchReviewMatches;
